import { useNavigate, useSearch } from '@tanstack/react-router';
import { CopyPlus } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { todayIn } from '@tt/shared';
import { Combobox, type ComboboxItem } from '@/components/combobox';
import { Button } from '@/components/ui/button';
import { formatDate, formatHours } from '@/lib/format';
import { useAdminUsers } from '@/lib/admin-queries';
import { useMe } from '@/lib/queries';
import { useFormat } from '@/lib/use-format';
import { CopyWeekDialog } from '@/features/time/copy-week-dialog';
import { TimeNav } from '@/features/time/time-nav';
import { WeekGrid } from '@/features/time/week-grid';

/**
 * Admin view of a user's week: review and correct their hours, and copy a week onto
 * another week of the same user. The selected user and week live in the URL.
 */
export function AdminTimesheetPage() {
  const { t } = useTranslation();
  const fmt = useFormat();
  const navigate = useNavigate();
  const { data: me } = useMe();
  const search = useSearch({ from: '/app/admin/timesheets' });
  const [query, setQuery] = useState('');
  const users = useAdminUsers({ q: query || undefined, pageSize: 50 });
  const [copyOpen, setCopyOpen] = useState(false);

  const date = search.date ?? todayIn(me?.timeZone ?? 'UTC');
  const userId = search.userId;
  const selected = users.data?.data.find((u) => u.id === userId);

  const items: ComboboxItem[] = useMemo(
    () =>
      (users.data?.data ?? []).map((u) => ({
        value: u.id,
        label: `${u.firstName} ${u.lastName}`,
        hint: u.status === 'ACTIVE' ? u.email : t(`admin.${u.status.toLowerCase()}`),
      })),
    [users.data, t],
  );

  const setSearch = (next: { userId?: string; date?: string }) =>
    void navigate({ to: '/admin/timesheets', search: { userId, date, ...next } });

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-lg font-semibold">{t('admin.timesheets')}</h1>
        <p className="text-sm text-muted-foreground">{t('admin.timesheetsHelp')}</p>
      </header>

      <div className="max-w-sm">
        <Combobox
          value={userId ?? null}
          onChange={(v) => setSearch({ userId: v ?? undefined })}
          items={items}
          placeholder={t('admin.selectUser')}
          searchPlaceholder={t('admin.searchUsers')}
          aria-label={t('admin.selectUser')}
          onSearchChange={setQuery}
        />
      </div>

      {!userId ? (
        <p className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
          {t('admin.noUserSelected')}
        </p>
      ) : (
        <>
          <WeekGrid
            date={date}
            userId={userId}
            header={(week) => (
              <TimeNav
                view="week"
                anchor={date}
                title={
                  selected
                    ? t('admin.viewingUser', { name: `${selected.firstName} ${selected.lastName}` })
                    : t('time.weekView')
                }
                total={formatHours(week?.weekTotal ?? 0, fmt)}
                onNavigate={(next) => setSearch({ date: next })}
                subtitle={
                  week ? t('time.weekOf', { date: formatDate(week.weekStart, fmt) }) : undefined
                }
                hideViewSwitcher
                headingLevel={2}
              >
                <Button variant="secondary" onClick={() => setCopyOpen(true)}>
                  <CopyPlus /> {t('time.copy.action')}
                </Button>
              </TimeNav>
            )}
          />
          <CopyWeekDialog
            open={copyOpen}
            onOpenChange={setCopyOpen}
            sourceDate={date}
            userId={userId}
            userName={selected ? `${selected.firstName} ${selected.lastName}` : undefined}
            onCopied={(result) => setSearch({ date: result.targetWeekStart })}
          />
        </>
      )}
    </div>
  );
}
